import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AdminUserPicker, type AdminUserOption } from '@/components/admin/AdminUserPicker';
import { ADMIN_USER_CPM_WALLET } from '@/queries/queries';
import {
  useAdminCreateCpmPayoutMutation,
  AdminCpmPayout,
} from '@/generated/graphql';
import { formatCents } from '@/lib/format';
import { PAYOUT_FEE_PERCENT, calcPayoutFee, calcPayoutNet } from '@/lib/payout/constants';

type UserOption = AdminUserOption;

interface RegisterPayoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialUser?: UserOption | null;
  lockUser?: boolean;
  onCreated?: (payout: AdminCpmPayout) => void;
}

export function RegisterPayoutDialog({
  open,
  onOpenChange,
  initialUser,
  lockUser,
  onCreated,
}: RegisterPayoutDialogProps) {
  const { t } = useTranslation();
  const [user, setUser] = useState<UserOption | null>(initialUser || null);
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(() =>
    new Date().toISOString().slice(0, 16),
  );
  const [note, setNote] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: walletData } = useQuery(ADMIN_USER_CPM_WALLET, {
    variables: { userId: user?.id || '' },
    skip: !user?.id,
    fetchPolicy: 'cache-and-network',
  });
  const availableCents = walletData?.adminUserCpmWallet?.available_cents ?? null;

  const [createPayout, { loading: creating }] = useAdminCreateCpmPayoutMutation();

  useEffect(() => {
    if (open) {
      setUser(initialUser || null);
      setAmount('');
      setPaidAt(new Date().toISOString().slice(0, 16));
      setNote('');
      setErrorMessage(null);
    }
  }, [open, initialUser]);

  const amountCents = useMemo(() => {
    const n = parseFloat(amount.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }, [amount]);

  const overBalance =
    availableCents !== null && amountCents > availableCents;

  const canSubmit =
    !!user && amountCents > 0 && !overBalance && !creating;

  const handleSubmit = async () => {
    if (!user) return;
    setErrorMessage(null);
    try {
      const result = await createPayout({
        variables: {
          input: {
            userId: user.id,
            amount_cents: amountCents,
            currency: 'BRL',
            note: note || null,
            paid_at: new Date(paidAt).toISOString(),
          },
        },
      });
      if (result.data?.adminCreateCpmPayout) {
        onCreated?.(result.data.adminCreateCpmPayout);
      }
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error';
      if (msg.includes('payout_exceeds_available_balance')) {
        setErrorMessage(t('admin.payouts.dialog.overBalance'));
      } else {
        setErrorMessage(msg);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.payouts.dialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!lockUser && (
            <div className="space-y-2">
              <Label>{t('admin.payouts.dialog.userLabel')}</Label>
              <AdminUserPicker
                value={user}
                onChange={setUser}
                placeholder={t('admin.payouts.dialog.userPlaceholder')}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('admin.payouts.dialog.amountLabel')}</Label>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              inputMode="decimal"
            />
            {amountCents > 0 && (
              <div className="rounded-md bg-muted/50 border px-3 py-2.5 space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Valor bruto</span>
                  <span>{formatCents(amountCents, 'BRL')}</span>
                </div>
                <div className="flex justify-between text-xs text-amber-500">
                  <span>Taxa de saque ({PAYOUT_FEE_PERCENT}%)</span>
                  <span>− {formatCents(calcPayoutFee(amountCents), 'BRL')}</span>
                </div>
                <div className="flex justify-between text-xs font-semibold border-t border-border/50 pt-1 mt-1">
                  <span>Valor a pagar ao clipador</span>
                  <span className="text-emerald-400">{formatCents(calcPayoutNet(amountCents), 'BRL')}</span>
                </div>
              </div>
            )}
            {availableCents !== null && (
              <p className="text-xs text-muted-foreground">
                {t('admin.payouts.dialog.availableHint', {
                  amount: formatCents(availableCents, 'BRL'),
                })}
              </p>
            )}
            {overBalance && (
              <p className="text-xs text-destructive">
                {t('admin.payouts.dialog.overBalance')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('admin.payouts.dialog.paidAtLabel')}</Label>
            <Input
              type="datetime-local"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('admin.payouts.dialog.noteLabel')}</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>

          {errorMessage && (
            <p className="text-sm text-destructive">{errorMessage}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('admin.payouts.dialog.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {t('admin.payouts.dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
